import { useEffect, useRef, useState } from 'react'

import tokenFetcher from 'common/auth/tokenFetcher'

import { CustomerJourney } from '../BriefAnalysis'

export interface BriefAnalysisRequirement {
  requirement: string
  fulfilled: boolean
  score: number
  reasoning: string
}

export interface BriefAnalysis {
  requirements: BriefAnalysisRequirement[]
  overall_score: number
}

export interface BriefAnalysisResult {
  brief_analysis: BriefAnalysis
  topics: string[]
}

interface UseDebouncedBriefAnalysisParams {
  briefText: string
  customerJourneyPayload: CustomerJourney
  contentId?: string
  enabled?: boolean
}

export const useDebouncedBriefAnalysis = ({
  briefText,
  customerJourneyPayload,
  contentId,
  enabled = true,
}: UseDebouncedBriefAnalysisParams) => {
  const abortControllerRef = useRef<AbortController | null>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [analysisResult, setAnalysisResult] =
    useState<BriefAnalysisResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    // Don't run if disabled or no content
    // But don't clear existing analysisResult when disabled - keep it for display
    if (!enabled || !contentId || !briefText.trim()) {
      setIsLoading(false)
      return
    }

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new AbortController for this request
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    setIsLoading(true)
    setError(null)

    // Set up debounce timer (3 seconds = 3000ms)
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const token: string = await tokenFetcher.getAccessToken()
        const response = await fetch(
          `https://prompt-management.eu1.jacquard.com/qa/accounts/56f16412c3351a8442cbcdf0/campaigns/${contentId}/analyze-brief`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-account-id': '56f16412c3351a8442cbcdf0',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              brief: briefText,
              customer_journey: {
                journey_title: customerJourneyPayload?.customerJourneyTitle,
                requirements: customerJourneyPayload?.requirements,
              },
            }),
            signal: abortController.signal,
          }
        )

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result: BriefAnalysisResult = await response.json()
        setAnalysisResult(result)
        setIsLoading(false)
      } catch (error: any) {
        // Ignore abort errors (expected when cancelling)
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          // eslint-disable-next-line no-console
          console.error('Error analyzing brief:', error)
          setError(error)
          setIsLoading(false)
        }
      }
    }, 1500)

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [briefText, customerJourneyPayload, contentId, enabled])

  return { analysisResult, isLoading, error }
}
